import { useEffect, useState, useMemo } from "react";
import { api, fmtINR } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple, Trash, ArrowCounterClockwise, Upload, DownloadSimple } from "@phosphor-icons/react";
import { UNITS } from "@/lib/units";
import { hsnToTax, guessKind } from "@/lib/hsn";
import { API_BASE } from "@/lib/api";
import ActionsMenu from "@/components/ActionsMenu";

async function downloadFile(url, filename) {
  const tok = localStorage.getItem("smipl_token");
  const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
  if (!res.ok) { toast.error("Download failed"); return; }
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  URL.revokeObjectURL(a.href);
}

const TAX_OPTIONS = [
  { value: 0, label: "0% (Nil)" }, { value: 5, label: "5%" }, { value: 12, label: "12%" },
  { value: 18, label: "18% (Default)" }, { value: 28, label: "28%" },
];
const UNITS_ALIAS = UNITS;

const initials = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
const avatarColor = (name) => {
  const c = ["bg-red-100 text-red-700", "bg-green-100 text-green-700", "bg-blue-100 text-blue-700",
             "bg-purple-100 text-purple-700", "bg-orange-100 text-orange-700", "bg-pink-100 text-pink-700"];
  const idx = (name || "").split("").reduce((a, ch) => a + ch.charCodeAt(0), 0) % c.length;
  return c[idx];
};

// Heuristic: SAC (Services Accounting Code) starts with 99. HSN (Harmonized) is anything else.
const guessCodeKind = (code) => guessKind(code);

export default function Products() {
  const { user } = useAuth();
  const canManage = hasRole(user, "super_admin", "hr");
  const canDelete = hasRole(user, "super_admin");
  const [tab, setTab] = useState("items");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  const load = () => api.get("/products", { params: { q: q || undefined, deleted: showDeleted || undefined } })
    .then((r) => { setItems(r.data); setSelected(new Set()); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, showDeleted]);

  const categories = useMemo(() => {
    const set = new Set();
    items.forEach((p) => (p.oem || p.category) && set.add(p.oem || p.category));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!cat) return items;
    return items.filter((p) => (p.oem || p.category) === cat);
  }, [items, cat]);

  const restore = async (p) => {
    await api.put(`/products/${p.id}`, { status: "active" });
    toast.success("Restored"); load();
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/products/${p.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="products-page" className="space-y-4">
      <PageHeader title={<>Products &amp; Services <span className="text-sm opacity-60 font-normal ml-2">{items.length} items</span></>}
                  description="Catalog of products and services SMIPL offers." />

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setShowDeleted(v === "deleted"); setCat(""); }}>
        <TabsList>
          <TabsTrigger value="items" data-testid="products-tab-items">Items</TabsTrigger>
          <TabsTrigger value="categories" data-testid="products-tab-categories">OEMs</TabsTrigger>
          <TabsTrigger value="price_lists" data-testid="products-tab-price-lists">Price Lists</TabsTrigger>
          <TabsTrigger value="deleted" data-testid="products-tab-deleted">Deleted</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-3 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 flex-1 max-w-md bg-white dark:bg-slate-800 border rounded-md px-3 py-1.5">
              <MagnifyingGlass size={16} className="opacity-40" />
              <input placeholder="Search products, category, description…" value={q} onChange={(e) => setQ(e.target.value)}
                     className="bg-transparent outline-none text-sm w-full" data-testid="products-search" />
            </div>
            <Select value={cat || "__all__"} onValueChange={(v) => setCat(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Select OEM" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All OEMs</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex-1" />
            {canManage && (
              <ActionsMenu
                entityLabel="Products"
                selectedCount={selected.size}
                canDestructive={canDelete}
                canManage={canManage}
                onImport={() => setShowImport(true)}
                onDownloadExcel={() => downloadFile(`${API_BASE}/products/export/excel`, "products.xlsx")}
                onDownloadPdf={() => downloadFile(`${API_BASE}/products/export/pdf`, "products.pdf")}
                onDownloadCsv={() => downloadFile(`${API_BASE}/products/export/csv`, "products.csv")}
                onBulkEdit={() => setShowBulkEdit(true)}
                onBulkDelete={async () => {
                  try { const r = await api.post("/products/bulk-delete", { ids: Array.from(selected) });
                    toast.success(`Deleted ${r.data.deleted} item(s)`); load(); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
                onDeleteAll={async () => {
                  try { const r = await api.post("/products/delete-all");
                    toast.success(`Deleted all ${r.data.deleted} item(s)`); load(); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
              />
            )}
            {canManage && <ProductDialog onSaved={load} />}
          </div>

          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40">
                <tr>
                  {canDelete && (
                    <th className="w-10 px-3 py-2">
                      <input type="checkbox" className="h-4 w-4 accent-[#fd7e14]"
                             checked={selected.size === filtered.length && filtered.length > 0}
                             onChange={() => {
                               if (selected.size === filtered.length) setSelected(new Set());
                               else setSelected(new Set(filtered.map((p) => p.id)));
                             }}
                             data-testid="products-select-all" />
                    </th>
                  )}
                  <th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Selling Price</th><th className="text-left px-3 py-2">Purchase Price</th><th className="text-left px-3 py-2">HSN/SAC</th><th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={canDelete ? 6 : 5} className="text-center py-10 opacity-60">No items.</td></tr>}
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t">
                    {canDelete && (
                      <td className="px-3 py-3">
                        <input type="checkbox" className="h-4 w-4 accent-[#fd7e14]"
                               checked={selected.has(p.id)}
                               onChange={() => {
                                 const s = new Set(selected); s.has(p.id) ? s.delete(p.id) : s.add(p.id); setSelected(s);
                               }}
                               data-testid={`product-check-${p.id}`} />
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${avatarColor(p.name)}`}>{initials(p.name)}</div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{p.name}</div>
                          <div className="text-xs opacity-70">
                            <span className="capitalize">{p.kind || "product"}</span>
                            {p.oem && <> · <span>{p.oem}</span></>}
                            {p.code && <> · <span className="font-mono">{p.code}</span></>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-mono">{fmtINR(p.selling_price || 0)}</div>
                      <div className="text-[10px] opacity-60">{p.tax_pct ?? 18}% {p.price_inclusive_tax ? "incl." : "excl."} tax</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-mono">{fmtINR(p.purchase_price || 0)}</div>
                      <div className="text-[10px] opacity-60">{p.tax_pct ?? 18}% incl. tax</div>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">{p.hsn_sac || "—"}</td>
                    <td className="px-3 py-3 text-right">
                      {canManage && <ProductDialog editing={p} onSaved={load} trigger={<Button size="sm" variant="outline"><PencilSimple size={12} className="mr-1" />Edit</Button>} />}
                      {canDelete && <Button size="sm" variant="ghost" className="ml-1" onClick={() => remove(p)}><Trash size={14} /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <Card className="p-4">
            <div className="text-sm opacity-70 mb-3">Categories inferred from your item catalog.</div>
            <div className="flex flex-wrap gap-2">
              {categories.length === 0 && <div className="text-xs opacity-60">No categories yet.</div>}
              {categories.map((c) => (
                <Badge key={c} variant="outline" className="text-sm">{c} <span className="ml-1 opacity-60 text-xs">{items.filter((p) => p.category === c).length}</span></Badge>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="price_lists" className="mt-4">
          <Card className="p-8 text-center opacity-70">
            <div className="text-sm">Price Lists — coming soon.</div>
            <div className="text-xs mt-1">Manage customer-specific pricing groups.</div>
          </Card>
        </TabsContent>

        <TabsContent value="deleted" className="mt-4">
          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40">
                <tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">HSN/SAC</th><th className="w-24" /></tr>
              </thead>
              <tbody>
                {filtered.filter((p) => p.status === "inactive").length === 0 && <tr><td colSpan="4" className="text-center py-10 opacity-60">No deleted items.</td></tr>}
                {filtered.filter((p) => p.status === "inactive").map((p) => (
                  <tr key={p.id} className="border-t opacity-70">
                    <td className="px-3 py-3">{p.name}</td>
                    <td className="px-3 py-3">{p.category || "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs">{p.hsn_sac || "—"}</td>
                    <td className="px-3 py-3 text-right">
                      {canManage && <Button size="sm" variant="outline" onClick={() => restore(p)}><ArrowCounterClockwise size={12} className="mr-1" />Restore</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
      <ImportProductsDialog controlledOpen={showImport} setControlledOpen={setShowImport} onDone={load} />
      <BulkEditProductsDialog open={showBulkEdit} onOpenChange={setShowBulkEdit} ids={Array.from(selected)} onDone={load} />
    </div>
  );
}

// ---------------- Product Dialog ----------------
const emptyForm = {
  name: "", code: "", kind: "product", category: "", hsn_sac: "", oem: "",
  description: "", selling_price: 0, purchase_price: 0, tax_pct: 18,
  price_inclusive_tax: false, primary_unit: "nos", barcode: "", status: "active",
};

function ProductDialog({ editing, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [oems, setOems] = useState([]);
  useEffect(() => { if (open) api.get("/oems").then((r) => setOems(r.data)); }, [open]);
  useEffect(() => { if (open) setForm(editing ? { ...emptyForm, ...editing } : emptyForm); }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const codeHint = guessCodeKind(form.hsn_sac);
  const codeMismatch = codeHint && codeHint !== form.kind;

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error("Name & code required"); return; }
    const payload = { ...form };
    ["selling_price", "purchase_price", "tax_pct"].forEach((k) => { payload[k] = Number(payload[k] || 0); });
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const autoBarcode = () => set("barcode", String(Math.floor(1e12 + Math.random() * 9e12)));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button className="btn-brand" data-testid="new-item-btn"><Plus size={16} className="mr-1.5" />New Item</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>

        <div className="text-sm font-semibold uppercase opacity-70 mb-2">Basic Details</div>
        <div className="flex gap-1 mb-3" role="tablist">
          <button type="button" onClick={() => set("kind", "product")}
                  data-testid="kind-product"
                  className={`px-4 py-1.5 rounded-md text-sm font-medium ${form.kind === "product" ? "bg-blue-600 text-white" : "bg-slate-100 dark:bg-slate-800"}`}>Product</button>
          <button type="button" onClick={() => set("kind", "service")}
                  data-testid="kind-service"
                  className={`px-4 py-1.5 rounded-md text-sm font-medium ${form.kind === "service" ? "bg-blue-600 text-white" : "bg-slate-100 dark:bg-slate-800"}`}>Service</button>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label>{form.kind === "service" ? "Service" : "Product"} Name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={`Enter ${form.kind} name`} data-testid="item-name-input" /></div>
          <div><Label>Part / Item Code *</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} /></div>
          <div><Label>OEM</Label>
            <input list="oem-list" value={form.oem || ""}
                   onChange={(e) => { const v = e.target.value; set("oem", v); set("category", v); }}
                   placeholder="Pick or type OEM"
                   className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            <datalist id="oem-list">{oems.map((o) => <option key={o.id} value={o.name} />)}</datalist>
          </div>
          <div><Label>Selling Price</Label>
            <div className="flex gap-1">
              <Input type="number" step="0.01" value={form.selling_price} onChange={(e) => set("selling_price", e.target.value)} placeholder="₹" />
              <Select value={form.price_inclusive_tax ? "incl" : "excl"} onValueChange={(v) => set("price_inclusive_tax", v === "incl")}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="excl">excl. Tax</SelectItem><SelectItem value="incl">with Tax</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Tax % *</Label><Select value={String(form.tax_pct)} onValueChange={(v) => set("tax_pct", Number(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{TAX_OPTIONS.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}</SelectContent>
          </Select></div>
          <div><Label>Primary Unit</Label>
            <input list="unit-list" value={form.primary_unit} onChange={(e) => set("primary_unit", e.target.value)}
                   placeholder="Search units…"
                   className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            <datalist id="unit-list">{UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}</datalist>
          </div>
          <div><Label>Purchase Price</Label><Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} placeholder="₹" /></div>
        </div>

        <div className="text-sm font-semibold uppercase opacity-70 mt-4 mb-2">Additional Information <span className="text-xs opacity-60 normal-case font-normal ml-2">Optional</span></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>HSN / SAC</Label>
            <Input value={form.hsn_sac}
                   onChange={(e) => {
                     const v = e.target.value; set("hsn_sac", v);
                     const rate = hsnToTax(v); if (rate !== null) set("tax_pct", rate);
                     const k = guessKind(v); if (k) set("kind", k);
                   }}
                   placeholder="HSN / SAC" data-testid="hsn-sac-input" />
            <a href="https://services.gst.gov.in/services/searchhsnsac" target="_blank" rel="noreferrer"
               className="text-xs text-blue-600 hover:underline mt-1 inline-block">Click here to check GST approved HSN/SAC codes.</a>
            {codeMismatch && (
              <div className="text-xs text-amber-600 mt-1" data-testid="hsn-mismatch-warn">
                ⚠ This code looks like a <b>{codeHint === "service" ? "Service (SAC)" : "Product (HSN)"}</b> code but you selected <b>{form.kind}</b>.
              </div>
            )}
          </div>
          <div><Label>Barcode</Label>
            <div className="flex gap-1">
              <Input value={form.barcode} onChange={(e) => set("barcode", e.target.value)} placeholder="0000000000000" />
              <Button type="button" variant="outline" onClick={autoBarcode}>Auto Generate</Button>
            </div>
          </div>
          <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
          </Select></div>
          <div className="md:col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} className="btn-brand" data-testid="item-save">{editing ? "Update" : "Add Item"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Import Products from Excel ----------------

function ImportProductsDialog({ onDone, controlledOpen, setControlledOpen }) {
  const [open, setOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const effOpen = isControlled ? controlledOpen : open;
  const setEffOpen = isControlled ? setControlledOpen : setOpen;
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    const tok = localStorage.getItem("smipl_token");
    const res = await fetch(`${API_BASE}/products/import/template`, { headers: { Authorization: `Bearer ${tok}` } });
    if (!res.ok) { toast.error("Template download failed"); return; }
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "products_import_template.xlsx"; a.click(); URL.revokeObjectURL(a.href);
  };

  const submit = async () => {
    if (!file) { toast.error("Choose an Excel file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} product(s)`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={effOpen} onOpenChange={(v) => { setEffOpen(v); if (!v) { setFile(null); setResult(null); } }}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline" data-testid="import-excel-btn"><Upload size={16} className="mr-1.5" />Import from Excel</Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Import Products &amp; Services from Excel</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border p-3 text-sm" style={{ borderColor: "hsl(var(--border))" }}>
            <div className="font-medium mb-1">Step 1 — Download the template</div>
            <div className="text-xs opacity-70 mb-2">Excel with the 19 required columns and 2 sample rows.</div>
            <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <DownloadSimple size={14} className="mr-1.5" /> Download Template
            </Button>
          </div>
          <div className="rounded-md border p-3 text-sm" style={{ borderColor: "hsl(var(--border))" }}>
            <div className="font-medium mb-1">Step 2 — Fill and upload</div>
            <div className="text-xs opacity-70 mb-2">Column order matters. Type must be 'product' or 'service'. Show Online / Not For Sale / Delete accept Yes/No.</div>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
          </div>
          {result && (
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div><b>{result.created}</b> item(s) created, <b>{result.skipped}</b> skipped.</div>
              {result.errors?.length > 0 && (
                <details className="text-xs opacity-80">
                  <summary className="cursor-pointer">{result.errors.length} error(s) — click to expand</summary>
                  <ul className="pl-4 mt-1 list-disc">
                    {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEffOpen(false)}>Close</Button>
          <Button onClick={submit} disabled={!file || busy} className="btn-brand" data-testid="import-run-btn">
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkEditProductsDialog({ open, onOpenChange, ids, onDone }) {
  const [upd, setUpd] = useState({ tax_pct: "", primary_unit: "", oem: "", status: "" });
  const submit = async () => {
    const payload = {};
    if (upd.tax_pct !== "") payload.tax_pct = Number(upd.tax_pct);
    if (upd.primary_unit) payload.primary_unit = upd.primary_unit;
    if (upd.oem) payload.oem = upd.oem;
    if (upd.status) payload.status = upd.status;
    if (!Object.keys(payload).length) { toast.error("Fill at least one field"); return; }
    try {
      const r = await api.post("/products/bulk-update", { ids, updates: payload });
      toast.success(`Updated ${r.data.updated} item(s)`); onOpenChange(false);
      setUpd({ tax_pct: "", primary_unit: "", oem: "", status: "" });
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Bulk edit {ids.length} item(s)</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="opacity-70">Only non-empty fields below will be applied.</p>
          <div><Label>Tax %</Label><Input type="number" value={upd.tax_pct} onChange={(e) => setUpd({ ...upd, tax_pct: e.target.value })} placeholder="e.g. 18" /></div>
          <div><Label>Unit</Label><Input value={upd.primary_unit} onChange={(e) => setUpd({ ...upd, primary_unit: e.target.value })} placeholder="e.g. nos" /></div>
          <div><Label>OEM / Category</Label><Input value={upd.oem} onChange={(e) => setUpd({ ...upd, oem: e.target.value })} /></div>
          <div><Label>Status</Label>
            <Select value={upd.status || "__none__"} onValueChange={(v) => setUpd({ ...upd, status: v === "__none__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="— no change —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— no change —</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="product-bulk-edit-submit">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

